import React, { useState } from "react";
import {
    ClipboardList,
    FileText,
    AlertTriangle,
    Layers,
    History,
    ArrowRight,
    ShieldAlert,
    Save,
    Send,
    Plus,
    Trash2,
    Anchor,
    Box,
    Globe
} from "lucide-react";
import coreApi from "../../utils/coreApi";

export default function CustomsEntryManager() {
    const [activeTab, setActiveTab] = useState("header");
    const [isDG, setIsDG] = useState(false);

    // State for audit trail and selected bin
    const [selectedBin, setSelectedBin] = useState(null);
    const [auditLogs, setAuditLogs] = useState([
        { id: 1, user: "Agent Smith", field: "Arrival Date", oldVal: "2026-03-10", newVal: "2026-03-12", time: "2 hours ago" },
        { id: 2, user: "Agent Smith", field: "Incoterm", oldVal: "CIF", newVal: "FOB", time: "1 day ago" }
    ]);

    // State for line items (Tariff Calculator)
    const [lines, setLines] = useState([
        { id: 1, description: "Industrial Pumps", hsCode: "8413.70", value: 5000, rate: 5, duty: 250, gst: 525, total: 775, isDG: false }
    ]);

    const addLine = () => {
        const newLine = { id: Date.now(), description: "", hsCode: "", value: 0, rate: 5, duty: 0, gst: 0, total: 0, isDG: false };
        setLines([...lines, newLine]);
    };

    const calculateLine = (idx, field, val) => {
        const newLines = [...lines];
        const line = { ...newLines[idx], [field]: val };

        // Logic: duty = value * rate, gst = (value + duty) * 0.10
        const value = parseFloat(line.value) || 0;
        const rate = parseFloat(line.rate) || 0;
        line.duty = value * (rate / 100);
        line.gst = (value + line.duty) * 0.10;
        line.total = line.duty + line.gst;

        newLines[idx] = line;
        setLines(newLines);
    };

    const totals = lines.reduce((acc, line) => ({
        duty: acc.duty + line.duty,
        gst: acc.gst + line.gst,
        total: acc.total + line.total
    }), { duty: 0, gst: 0, total: 0 });

    const renderHeader = () => (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 animate-in fade-in duration-300">
            <div className="space-y-6 bg-white p-6 rounded-2xl border border-slate-200">
                <h3 className="font-bold text-navy-900 border-b pb-2 flex items-center gap-2">
                    <FileText size={18} className="text-blue-600" /> Importer & References
                </h3>
                <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                        <label className="text-[10px] font-bold text-slate-400 uppercase">Importer Name</label>
                        <input type="text" className="w-full p-2 bg-slate-50 border rounded-lg text-sm" defaultValue="SME Global Pty Ltd" />
                    </div>
                    <div className="space-y-1">
                        <label className="text-[10px] font-bold text-slate-400 uppercase">ABN</label>
                        <input type="text" className="w-full p-2 bg-slate-50 border rounded-lg text-sm" defaultValue="51 123 456 789" />
                    </div>
                    <div className="col-span-2 space-y-1">
                        <label className="text-[10px] font-bold text-slate-400 uppercase">Broker Reference</label>
                        <input type="text" className="w-full p-2 bg-slate-50 border rounded-lg text-sm font-mono" defaultValue="BRK-2026-X12" />
                    </div>
                </div>
            </div>

            <div className="space-y-6 bg-white p-6 rounded-2xl border border-slate-200">
                <h3 className="font-bold text-navy-900 border-b pb-2 flex items-center gap-2">
                    <Anchor size={18} className="text-navy-900" /> Vessel & Voyage
                </h3>
                <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                        <label className="text-[10px] font-bold text-slate-400 uppercase">Vessel Name</label>
                        <input type="text" className="w-full p-2 bg-slate-50 border rounded-lg text-sm" defaultValue="COSCO SHIPPING" />
                    </div>
                    <div className="space-y-1">
                        <label className="text-[10px] font-bold text-slate-400 uppercase">Voyage</label>
                        <input type="text" className="w-full p-2 bg-slate-50 border rounded-lg text-sm" defaultValue="V245E" />
                    </div>
                    <div className="space-y-1">
                        <label className="text-[10px] font-bold text-slate-400 uppercase">Port of Loading</label>
                        <input type="text" className="w-full p-2 bg-slate-50 border rounded-lg text-sm" defaultValue="SHANGHAI" />
                    </div>
                    <div className="space-y-1">
                        <label className="text-[10px] font-bold text-slate-400 uppercase">Arrival Date</label>
                        <input type="date" className="w-full p-2 bg-slate-50 border rounded-lg text-sm" defaultValue="2026-03-12" />
                    </div>
                </div>
            </div>
        </div>
    );

    const renderLines = () => (
        <div className="space-y-6 animate-in fade-in duration-300">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="p-4 bg-slate-50/50 border-b border-slate-100 flex justify-between items-center">
                    <h3 className="font-bold text-navy-900 flex items-center gap-2 pt-1">
                        <Layers size={18} className="text-violet-600" /> Line Items & Tariff Calculation
                    </h3>
                    <button onClick={addLine} className="flex items-center gap-1.5 text-xs font-bold bg-navy-900 text-white px-3 py-1.5 rounded-lg hover:bg-navy-800 transition-colors">
                        <Plus size={14} /> Add Line
                    </button>
                </div>
                <table className="w-full text-left">
                    <thead className="text-[10px] font-bold text-slate-400 uppercase bg-slate-50/30">
                        <tr>
                            <th className="px-4 py-3">Description</th>
                            <th className="px-4 py-3">HS Code</th>
                            <th className="px-4 py-3 text-right">Value (USD)</th>
                            <th className="px-4 py-3 text-right">Duty %</th>
                            <th className="px-4 py-3 text-right">Duty ($)</th>
                            <th className="px-4 py-3 text-right">GST ($)</th>
                            <th className="px-4 py-3">DG</th>
                            <th className="px-4 py-3"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {lines.map((line, idx) => (
                            <tr key={line.id} className="hover:bg-slate-50 transition-colors">
                                <td className="px-4 py-4 min-w-[200px]">
                                    <input
                                        type="text"
                                        className="w-full p-1 border rounded text-sm"
                                        value={line.description}
                                        onChange={e => calculateLine(idx, 'description', e.target.value)}
                                    />
                                </td>
                                <td className="px-4 py-4">
                                    <input
                                        type="text"
                                        className="w-20 p-1 border rounded text-sm font-mono"
                                        value={line.hsCode}
                                        onChange={e => calculateLine(idx, 'hsCode', e.target.value)}
                                    />
                                </td>
                                <td className="px-4 py-4 text-right">
                                    <input
                                        type="number"
                                        className="w-24 p-1 border rounded text-sm text-right font-mono"
                                        value={line.value}
                                        onChange={e => calculateLine(idx, 'value', e.target.value)}
                                    />
                                </td>
                                <td className="px-4 py-4 text-right">
                                    <input
                                        type="number"
                                        className="w-14 p-1 border rounded text-sm text-right font-mono"
                                        value={line.rate}
                                        onChange={e => calculateLine(idx, 'rate', e.target.value)}
                                    />
                                </td>
                                <td className="px-4 py-4 text-right font-mono text-slate-600">${line.duty.toFixed(2)}</td>
                                <td className="px-4 py-4 text-right font-mono text-slate-600">${line.gst.toFixed(2)}</td>
                                <td className="px-4 py-4">
                                    <button
                                        onClick={() => {
                                            const newLines = [...lines];
                                            newLines[idx].isDG = !newLines[idx].isDG;
                                            setLines(newLines);
                                            if (newLines[idx].isDG) setIsDG(true);
                                            else if (!newLines.some(l => l.isDG)) setIsDG(false);
                                        }}
                                        className={`p-1.5 rounded-lg transition-colors ${line.isDG ? 'bg-red-100 text-red-600' : 'bg-slate-100 text-slate-400'}`}
                                    >
                                        <AlertTriangle size={16} />
                                    </button>
                                </td>
                                <td className="px-4 py-4 text-right">
                                    <button onClick={() => setLines(lines.filter(l => l.id !== line.id))} className="text-slate-300 hover:text-red-500"><Trash2 size={16} /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );

    const renderDG = () => (
        <div className="bg-white p-8 rounded-2xl border border-slate-200 animate-in slide-in-from-bottom-2 duration-300">
            {isDG ? (
                <div className="space-y-6">
                    <div className="bg-red-50 border border-red-200 p-4 rounded-xl flex gap-4 items-center">
                        <div className="w-12 h-12 bg-red-100 text-red-600 rounded-full flex items-center justify-center shrink-0">
                            <ShieldAlert size={24} />
                        </div>
                        <div>
                            <h4 className="font-bold text-red-700">Dangerous Goods Detected</h4>
                            <p className="text-sm text-red-600">This entry requires mandatory DG declarations and MSDS documentation for ABF compliance.</p>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="space-y-1">
                            <label className="text-[10px] font-bold text-slate-400 uppercase">UN Number</label>
                            <input type="text" className="w-full p-2 bg-slate-50 border rounded-lg text-sm" placeholder="e.g. 1993" />
                        </div>
                        <div className="space-y-1">
                            <label className="text-[10px] font-bold text-slate-400 uppercase">DG Class</label>
                            <select className="w-full p-2 bg-slate-50 border rounded-lg text-sm">
                                <option>Class 3 - Flammable Liquids</option>
                                <option>Class 8 - Corrosive Substances</option>
                                <option>Class 9 - Miscellaneous</option>
                            </select>
                        </div>
                        <div className="space-y-1">
                            <label className="text-[10px] font-bold text-slate-400 uppercase">Packing Group</label>
                            <input type="text" className="w-full p-2 bg-slate-50 border rounded-lg text-sm" placeholder="II" />
                        </div>
                    </div>
                </div>
            ) : (
                <div className="text-center py-12">
                    <Box size={40} className="mx-auto text-slate-200 mb-4" />
                    <h3 className="text-lg font-bold text-navy-900">No DG Items</h3>
                    <p className="text-slate-400 max-w-sm mx-auto mt-2">Flag items in the Line Items tab as 'Dangerous Goods' to enable special compliance handling.</p>
                </div>
            )}
        </div>
    );

    return (
        <div className="max-w-7xl mx-auto py-8 px-4 flex flex-col lg:flex-row gap-8">
            {/* Sidebar - Sticky Summary */}
            <div className="lg:w-80 order-2 lg:order-1">
                <div className="bg-white rounded-2xl border border-navy-900/10 shadow-xl overflow-hidden sticky top-8">
                    <div className="p-6 bg-navy-900 text-white">
                        <h3 className="font-bold text-lg flex items-center gap-2">
                            Duty Summary <CalculatorIcon />
                        </h3>
                        <p className="text-navy-300 text-xs mt-1">Calculated in USD based on current rates.</p>
                    </div>
                    <div className="p-6 space-y-4">
                        <div className="flex justify-between items-center text-sm font-medium text-slate-600">
                            <span>Total Duty</span>
                            <span className="font-mono text-navy-900">${totals.duty.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                        </div>
                        <div className="flex justify-between items-center text-sm font-medium text-slate-600">
                            <span>GST (10%)</span>
                            <span className="font-mono text-navy-900">${totals.gst.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                        </div>
                        <div className="pt-4 border-t border-slate-100 flex justify-between items-center">
                            <span className="font-bold text-navy-900">Total Payable</span>
                            <span className="font-bold text-xl text-navy-900 font-mono">${totals.total.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                        </div>
                        <div className="pt-6 space-y-3">
                            <button
                                onClick={async () => {
                                    try {
                                        // Using a dummy ID for demonstration as requested
                                        const dummyId = "00000000-0000-0000-0000-000000000000";
                                        await coreApi.post(`/api/declarations/${dummyId}/validate`);
                                        await coreApi.post(`/api/declarations/${dummyId}/submit`);
                                        alert("Successfully lodged with ABF via .NET Backend! 🟢");
                                    } catch (err) {
                                        console.error("Submission failed", err);
                                        alert("Lodge failed: Check .NET backend status.");
                                    }
                                }}
                                className="w-full bg-navy-900 text-white py-3 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-navy-800 transition-all shadow-lg shadow-navy-900/20 active:scale-[0.98]"
                            >
                                Lodge to ABF <Send size={18} />
                            </button>
                            <button className="w-full bg-white border border-slate-200 text-slate-700 py-3 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-slate-50 transition-all">
                                Save Draft <Save size={18} />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content Areas */}
            <div className="flex-1 order-1 lg:order-2">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
                    <div>
                        <div className="flex items-center gap-3 mb-1">
                            <h1 className="text-3xl font-bold text-navy-900 font-serif">Customs Entry</h1>
                            <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest leading-none pt-[5px]">Draft</span>
                        </div>
                        <p className="text-slate-500">Creating entry for <span className="text-navy-900 font-bold">SME Global Pty Ltd</span></p>
                    </div>
                    <div className="flex gap-2">
                        <button className="p-2.5 bg-white border border-slate-200 rounded-xl text-slate-400 hover:text-navy-900 transition-colors">
                            <History size={20} />
                        </button>
                        <button className="p-2.5 bg-white border border-slate-200 rounded-xl text-slate-400 hover:text-navy-900 transition-colors">
                            <Globe size={20} />
                        </button>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex gap-1 bg-slate-100 p-1.5 rounded-xl mb-8 w-fit shadow-inner">
                    {[
                        { id: "header", label: "Overview", icon: ClipboardList },
                        { id: "lines", label: "Line Items", icon: Layers },
                        { id: "dg", label: "Dangerous Goods", icon: AlertTriangle },
                        { id: "docs", label: "Documents", icon: Box },
                        { id: "audit", label: "Audit Trail", icon: History },
                    ].map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`px-5 py-2.5 rounded-lg text-sm font-bold flex items-center gap-2 transition-all ${activeTab === tab.id ? "bg-white text-navy-900 shadow-md" : "text-slate-500 hover:text-navy-700"
                                }`}
                        >
                            <tab.icon size={16} /> {tab.label}
                        </button>
                    ))}
                </div>

                <div className="min-h-[400px]">
                    {activeTab === "header" && renderHeader()}
                    {activeTab === "lines" && renderLines()}
                    {activeTab === "dg" && renderDG()}
                    {activeTab === "docs" && (
                        <div className="space-y-6 animate-in fade-in">
                            <div className="bg-amber-50 border border-amber-200 p-4 rounded-xl flex gap-4 items-center mb-6">
                                <div className="p-2 bg-amber-100 text-amber-700 rounded-lg">
                                    <ShieldCheck size={20} />
                                </div>
                                <div className="flex-1">
                                    <h4 className="text-sm font-bold text-amber-900">ATT Compliance Match</h4>
                                    <p className="text-xs text-amber-700">3 documents automatically pulled from Client's ATT Project #ATT-992.</p>
                                </div>
                                <button className="text-xs font-bold text-amber-900 hover:underline">Sync Now</button>
                            </div>
                            <div className="text-center py-20 bg-white rounded-2xl border border-dashed border-slate-200">
                                <Box size={40} className="mx-auto text-slate-300 mb-4" />
                                <h3 className="text-lg font-bold text-navy-900">Document Management</h3>
                                <p className="text-slate-400">Bill of Lading, Invoice, and Packing List uploads.</p>
                                <button className="mt-4 text-sm font-bold text-blue-600 hover:underline">Upload Files</button>
                            </div>
                        </div>
                    )}
                    {activeTab === "audit" && (
                        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm animate-in fade-in">
                            <div className="p-6 border-b border-slate-100 bg-slate-50/50">
                                <h3 className="font-bold text-navy-900 flex items-center gap-2">
                                    <History size={20} className="text-navy-700" /> Audit History
                                </h3>
                            </div>
                            <div className="divide-y divide-slate-100">
                                {auditLogs.map(log => (
                                    <div key={log.id} className="p-6 flex items-start gap-4">
                                        <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-[10px] font-bold text-slate-500">
                                            {log.user.split(' ').map(n => n[0]).join('')}
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex justify-between items-center mb-1">
                                                <span className="text-sm font-bold text-navy-900">{log.user}</span>
                                                <span className="text-xs text-slate-400">{log.time}</span>
                                            </div>
                                            <p className="text-xs text-slate-600">
                                                Changed <span className="font-bold text-navy-700">{log.field}</span> from
                                                <span className="mx-1 px-1.5 py-0.5 bg-red-50 text-red-700 rounded border border-red-100 italic">{log.oldVal}</span>
                                                to
                                                <span className="mx-1 px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded border border-emerald-100 font-bold">{log.newVal}</span>
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function CalculatorIcon() {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-calculator"><rect width="16" height="20" x="4" y="2" rx="2" /><line x1="8" x2="16" y1="6" y2="6" /><line x1="16" x2="16" y1="14" y2="18" /><path d="M16 10h.01" /><path d="M12 10h.01" /><path d="M8 10h.01" /><path d="M12 14h.01" /><path d="M8 14h.01" /><path d="M12 18h.01" /><path d="M8 18h.01" /></svg>
    )
}
